import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { loginDefaults } from "./config";
import { createLoginSchema, type LoginFormValues } from "./schema";
import elistechLogo from "~/assets/elistech-logo.svg";
import { Button } from "~/components/ui/Button";
import { Field } from "~/components/ui/Field";
import { LoadingIndicator } from "~/components/ui/LoadingIndicator";
import { useAuth } from "~/features/auth/hooks";
import { useTranslation } from "~/i18n";

export function LoginPage() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    clearErrors,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setError,
  } = useForm<LoginFormValues>({
    defaultValues: {
      email: loginDefaults.email,
      password: loginDefaults.password,
    },
    resolver: zodResolver(createLoginSchema(t)),
  });

  async function onSubmit({ email, password }: LoginFormValues) {
    clearErrors("root");
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch {
      setError("root", {
        message: t("loginScreen.loginError"),
      });
    }
  }

  return (
    <main className="brand-gradient flex min-h-dvh px-5 py-10 sm:px-8 sm:py-12">
      <div className="m-auto flex w-full max-w-[500px] flex-col items-center">
        <img
          src={elistechLogo}
          alt="ElisTech"
          className="mb-12 h-auto w-48 max-w-[65vw]"
        />

        <form
          noValidate
          aria-busy={isSubmitting}
          onSubmit={handleSubmit(onSubmit)}
          className="w-full rounded-lg bg-surface p-8 shadow-sm sm:p-10 lg:p-14"
        >
          <h1 className="mb-10 text-center text-2xl font-semibold tracking-tight">
            ElisTech Portal
          </h1>

          <div className="space-y-7">
            <Field
              label={t("loginScreen.email")}
              type="email"
              autoComplete="email"
              error={errors.email?.message}
              {...register("email")}
              required
            />
            <Field
              label={t("loginScreen.password")}
              type="password"
              autoComplete="current-password"
              error={errors.password?.message}
              {...register("password")}
              required
            />
          </div>

          {errors.root?.message && (
            <p role="alert" className="mt-5 text-sm font-medium text-danger">
              {errors.root.message}
            </p>
          )}

          <Button
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            className="mt-8 w-full gap-2"
          >
            {isSubmitting && <LoadingIndicator />}
            <span aria-live="polite">
              {t(isSubmitting ? "loginScreen.signingIn" : "loginScreen.signIn")}
            </span>
          </Button>
        </form>
      </div>
    </main>
  );
}
